import { Component, Input, ViewChild, ElementRef, OnChanges, OnInit, Output, EventEmitter } from '@angular/core';
import { Observable } from 'rxjs';
import {
    Note,
    OfferNotes,
    OfferType,
    OfferNoteAndCategory,
    CandidateOfferNoteSourceEnum
} from 'src/app/shared/models/offers.model';
import { FeatureFlag } from '../../models/enums/feature-flag';
import { OffersUserType } from 'src/app/shared/models/enums/offers.enum';
import { LDFeatureManager } from 'src/app/shared/services/ld-feature-manager';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { CommonModule } from '@angular/common';
import { MatChipsModule } from '@angular/material/chips';
import { MatRadioModule } from '@angular/material/radio';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatDividerModule } from '@angular/material/divider';
import { MatInputModule } from '@angular/material/input';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { RtoDatesHelpers } from 'src/app/shared/helpers/helpers';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { VmsSubmittalTypes } from 'src/app/accountmanagers/enums/vms-submittal-types.enum';

interface OfferNoteFormGroup {
    noteCategory: FormControl<number>;
    noteText: FormControl<string>;
}

@UntilDestroy()
@Component({
    selector: 'nova-shared-notes',
    templateUrl: './notes-display.component.html',
    styleUrls: ['./notes-display.component.scss'],
    standalone: true,
    imports: [
        CommonModule,
        MatChipsModule,
        MatRadioModule,
        MatButtonModule,
        MatFormFieldModule,
        MatDividerModule,
        MatInputModule,
        ReactiveFormsModule,
        MatIconModule,
        MatTooltipModule
    ]
})
export class SharedNotesComponent implements OnChanges, OnInit {
    readonly featureFlag = FeatureFlag;
    notes: Observable<Note[]>;
    @Input() agencyNotes: OfferNotes[];
    @Input() isVMS?: boolean;
    @Input() vmsType?: number;
    @Input() offerType: OfferType;
    @Input() userType?: OffersUserType;
    @Input() headingTitle = '';
    @Input() headingTitleSubtext = '';
    @Output() noteAdded = new EventEmitter<string>();
    @Output() noteWithCategoryAdded = new EventEmitter<OfferNoteAndCategory>();
    @ViewChild('scrollMe', { static: false }) private myScrollContainer: ElementRef;

    readonly RtoDatesHelpers = RtoDatesHelpers;
    isRecruiterSpecificOfferNoteFlagEnabled = false;
    selectedNoteSourceFilter = new FormControl<number[]>([1]);
    offerNoteForm: FormGroup<OfferNoteFormGroup>;
    isDisabled = true;

    featureOptimizationEnabled = this._featureManager.isEnabledSignal(FeatureFlag.OfferOptimizations);
    CandidateOfferNoteSourceEnum = CandidateOfferNoteSourceEnum;
    impersonationActivityTrackingEnabled = false;

    constructor(
        private readonly _featureManager: LDFeatureManager,
        private readonly fb: FormBuilder
    ) {}

    ngOnChanges(changes) {
        if (changes['agencyNotes'] && this.agencyNotes) {
            setTimeout(() => {
                this.scrollToBottom();
            }, 300);
        }
    }

    scrollToBottom(): void {
        try {
            if (!this.featureOptimizationEnabled()) {
                this.myScrollContainer.nativeElement.scrollTop = this.myScrollContainer.nativeElement.scrollHeight;
            }
        } catch (err) {
            console.log('ERROR: ', err);
        }
    }

    get filteredNotes(): OfferNotes[] {
        const selectedFilters = this.selectedNoteSourceFilter.value ?? [];
        if (selectedFilters.length === 0) {
            return this.agencyNotes;
        }
        return this.agencyNotes?.filter((note) => selectedFilters.includes(note.source));
    }

    get showNoteSourceTypePill(): boolean {
        const selectedFilters = this.selectedNoteSourceFilter.value ?? [];
        return selectedFilters.length !== 1;
    }

    ngOnInit() {
        this._featureManager
            .isEnabled(FeatureFlag.ConnectVMSRecruiterSpecificOfferNotes)
            .pipe(untilDestroyed(this))
            .subscribe((isEnabled) => {
                this.isRecruiterSpecificOfferNoteFlagEnabled = isEnabled;
            });

        this._featureManager
            .isEnabled(FeatureFlag.EnableOfferNoteImpersonationActivityTracking)
            .pipe(untilDestroyed(this))
            .subscribe((flag) => {
                this.impersonationActivityTrackingEnabled = flag;
            });

        if (this.featureOptimizationEnabled()) {
            this.offerNoteForm = this.fb.group({
                noteCategory: [2, Validators.required],
                noteText: ['', [Validators.required, Validators.maxLength(500)]]
            });
            this.offerNoteForm.statusChanges.subscribe(() => {
                this.isDisabled = this.offerNoteForm.invalid;
            });
        }
    }

    shouldDisplayCategoryChips(note: OfferNotes) {
        return (
            this.isRecruiterSpecificOfferNoteFlagEnabled &&
            this.offerType === OfferType.Direct &&
            this.userType &&
            (this.userType === OffersUserType.AccountManager || this.userType === OffersUserType.Recruiter) &&
            note.category?.length > 0
        );
    }

    get shouldDisplayNoteCategoryCheckbox(): boolean {
        return (
            this.userType === OffersUserType.AccountManager &&
            !!this.isVMS &&
            this.vmsType !== Number(VmsSubmittalTypes.Apn)
        );
    }

    addNote() {
        if (this.offerNoteForm.valid) {
            const noteText: string = this.offerNoteForm.value.noteText;
            const noteCategory: number = this.offerNoteForm.value.noteCategory;

            if (this.shouldDisplayNoteCategoryCheckbox) {
                const noteTextWithCategory: OfferNoteAndCategory = {
                    note: noteText,
                    category: noteCategory
                };
                this.noteWithCategoryAdded.emit(noteTextWithCategory);
            } else {
                this.noteAdded.emit(noteText);
            }

            this.offerNoteForm.controls.noteText.reset('');
        }
    }

    shouldDisplayTitleOnNewLine(note: OfferNotes) {
        return this.shouldDisplayCategoryChips(note) && note.title?.length > 0;
    }

    shouldUseImpersonationUi(): boolean {
        return this.impersonationActivityTrackingEnabled;
    }

    shouldShowImpersonationMask(note: OfferNotes): boolean {
        return this.shouldUseImpersonationUi() && !!note?.wasCreatedViaImpersonation;
    }

    getImpersonationTooltip(note: OfferNotes): string {
        const actionUser = note?.impersonatedByUserName;
        const actingAsUser = note?.fullName;

        if (!actionUser || !actingAsUser) {
            return '';
        }

        return `Action completed by ${actionUser} on behalf of ${actingAsUser}`;
    }
}
